import { Entity } from "@cayo/shared/api/types/cayo-graph";
import { IInteractiveItem } from "@cayo/shared/components/Alerts/types";
import logger from "@cayo/shared/libs/logger";
import {
  ALERTS_PANE_MAX_ITEMS,
  IUpdateInteractiveItemAction,
  NotificationAction,
} from "./notification.types";

const log = logger.getLogger("notification.api module");

export const updateInteractiveItemsReducer = (
  oldItems: IInteractiveItem[],
  action: IUpdateInteractiveItemAction
): IInteractiveItem[] => {
  if (
    action.action !== NotificationAction.Replace &&
    (!action.newItems || action.newItems.length === 0) &&
    action.action
  ) {
    return oldItems;
  }

  switch (action.action) {
    case NotificationAction.Add:
    case NotificationAction.Modify:
      if (action.newItems) {
        return modifyInteractiveItems(
          oldItems,
          action.newItems ?? [],
          action.maxItems,
          action.noSort,
          action.addNewToTail
        );
      }

      break;

    case NotificationAction.Remove:
      if (action.newItems) {
        return deleteInteractiveItems(oldItems, action.newItems);
      }
      break;

    case NotificationAction.RemoveAll:
      return [];

    case NotificationAction.Replace:
    default:
      return truncateItemsList(action.newItems ?? [], action.maxItems);
  }

  return oldItems;
};

export const mergeInteractiveItems = (
  oldItems: IInteractiveItem[],
  newItems: IInteractiveItem[],
  insertNewItems?: boolean | undefined,
  removeConditionHandler?: ((item: IInteractiveItem) => boolean) | undefined
) => {
  for (const newItem of newItems as any[]) {
    const existingItem = (oldItems as any[]).find((j) => j.id === newItem.id);
    if (existingItem) {
      if (removeConditionHandler && removeConditionHandler(existingItem)) {
        const deletingIndex = oldItems.indexOf(existingItem);
        oldItems.splice(deletingIndex, 1);
        continue;
      }

      for (const prop of Object.getOwnPropertyNames(newItem)) {
        if (newItem[prop] && newItem[prop] !== undefined && newItem[prop] !== null) {
          existingItem[prop] = newItem[prop];
        }
      }
    } else if (insertNewItems) {
      oldItems.unshift(newItem);
    }
  }
};
export const truncateItemsList = (
  items: IInteractiveItem[],
  maxItems?: number | undefined
): IInteractiveItem[] => {
  log.debug("truncateItemsList");
  return items.slice(0, maxItems ?? ALERTS_PANE_MAX_ITEMS);
};

export const modifyInteractiveItems = (
  oldItems: IInteractiveItem[],
  newItems: IInteractiveItem[],
  maxItems?: number | undefined,
  noSort?: boolean,
  addNewToTail?: boolean
) => {
  log.debug("modifyInteractiveItems");
  if (newItems.length === 0) {
    return [];
  }

  const now = new Date();

  for (const newItem of newItems as any[]) {
    const existingItem = (oldItems as any[]).find((j) => j.id === newItem.id);
    if (existingItem) {
      for (const prop of Object.getOwnPropertyNames(newItem)) {
        if (newItem[prop] !== undefined && newItem[prop] !== null) {
          existingItem[prop] = newItem[prop];
        }
      }
    } else {
      if (addNewToTail) {
        oldItems.push(newItem);
        log.debug("add alert to tail", newItem);
      } else {
        log.debug("add alert to head", newItem);
        oldItems.unshift(newItem);
      }
    }
  }
  if (!noSort && oldItems.length) {
    const isRisk = (oldItems[0] as Entity).objectType === "cayo.graph.riskAlert";

    if (isRisk) {
      oldItems = oldItems.sort((a, b) => ((b as any)?.severity || 0) - ((a as any)?.severity || 0));
    } else {
      oldItems = oldItems.sort(
        (a, b) => (b.modificationTime || now).getTime() - (a.modificationTime || now).getTime()
      );
    }
  }

  return truncateItemsList(oldItems, maxItems);
};

export const deleteInteractiveItems = (
  oldItems: IInteractiveItem[],
  toDeleteItems: IInteractiveItem[]
) => {
  log.debug("deleteInteractiveItems");
  return oldItems.filter((i) => toDeleteItems.findIndex((ii) => ii.id === i.id) === -1);
};
